import { Formik, FormikProps } from 'formik';
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import ButtonLoading from '@mui/lab/LoadingButton';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import Visibility from '@mui/icons-material/VisibilityOutlined';
import VisibilityOff from '@mui/icons-material/VisibilityOffOutlined';

import { valueSafeEcryptor } from 'utils/Encryptor';
import { ChangePasswordValidationSchema } from 'config/FormValidaton/TenantMaster/MasterTable/PersonTableValidationSchema';
import { useResetPasswordPerson } from 'services/v1/TenantMaster/MasterTable/PersonTableDataService';
import { PersonResetPasswordRequest } from 'types/api/TenantMaster/MasterTable/PersonTableDataTypes';
import { getErrorMessage } from 'utils/Error';
import { AxiosDefaultErrorEntity } from 'types';

const ModalContentStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  bgcolor: 'background.paper',
  minHeight: 350,
  borderRadius: 1,
  px: 3,
  py: 2,
};

const textInputStyles = {
  width: '55%',
  '& .MuiOutlinedInput': {
    padding: '1px 2px',
  },
};

const closeIconStyle = { color: '#98A2AE', cursor: 'pointer' };

interface FormValue {
  password: string;
  confirmPassword: string;
}

const formInitialValues: FormValue = {
  confirmPassword: '',
  password: '',
};

interface ModalResetPasswordProps {
  visible?: boolean;
  onClose?: () => void;
  userId?: string | null;
}

export function ModalResetPassword(props: ModalResetPasswordProps) {
  const formikRef = useRef<FormikProps<FormValue>>(null);
  const { onClose, visible = false } = props;
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handlePasswordToggle = () => {
    setShowPassword(!showPassword);
  };

  const handleConfirmPasswordToggle = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };
  const {
    mutate: resetPasswordPerson,
    isLoading: isUpdating,
    isError: isUpdatingError,
    error: errorCreating,
  } = useResetPasswordPerson();

  const handleOnCancel = () => {
    onClose?.();
    formikRef.current?.resetForm();
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const isSemiLoading = isUpdating;

  const handleOnSave = (data: FormValue) => {
    const payload: PersonResetPasswordRequest = {
      confirmPassword: valueSafeEcryptor(data.confirmPassword) || '',
      password: valueSafeEcryptor(data.password) || '',
      userId: props.userId || '',
    };
    resetPasswordPerson(payload, {
      onSuccess: () => {
        toast.success('Reset password successfully!');
        handleOnCancel();
      },
    });
  };

  return (
    <div>
      <Formik
        innerRef={formikRef}
        initialValues={formInitialValues}
        validationSchema={ChangePasswordValidationSchema}
        onSubmit={handleOnSave}
      >
        {(formikProps) => {
          const { handleSubmit, values, handleChange, errors, touched, handleBlur } = formikProps;
          return (
            <Modal open={visible} onClose={onClose}>
              <Stack direction='column' sx={ModalContentStyle} justifyContent='space-between'>
                <Stack direction='column'>
                  <Stack justifyContent='space-between' direction='row' alignItems='center' spacing={2}>
                    <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797' }}>
                      Reset Password
                    </Typography>
                    <IconButton onClick={onClose} component='label'>
                      <HighlightOffRoundedIcon sx={closeIconStyle} />
                    </IconButton>
                  </Stack>
                  <Divider sx={{ mb: 1 }} />
                  <Stack>
                    <Stack
                      direction='row'
                      spacing={2}
                      alignItems='center'
                      justifyContent='space-between'
                      sx={{ mt: 2 }}
                    >
                      <Typography variant='input-label'>New Password</Typography>
                      <TextField
                        sx={textInputStyles}
                        size='small'
                        disabled={isSemiLoading}
                        type={showPassword ? 'text' : 'password'}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={`User's password`}
                        value={values?.password}
                        name='password'
                        error={touched.password && Boolean(errors.password)}
                        helperText={touched.password && errors.password}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end'>
                              <IconButton onClick={handlePasswordToggle} size='small'>
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Stack>
                    <Stack
                      direction='row'
                      spacing={2}
                      alignItems='center'
                      justifyContent='space-between'
                      sx={{ mt: 2 }}
                    >
                      <Typography variant='input-label'>Confirm New Password</Typography>
                      <TextField
                        sx={textInputStyles}
                        size='small'
                        type={showConfirmPassword ? 'text' : 'password'}
                        disabled={isSemiLoading}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Please re-enter user's password"
                        value={values?.confirmPassword}
                        name='confirmPassword'
                        error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                        helperText={touched.confirmPassword && errors.confirmPassword}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end'>
                              <IconButton onClick={handleConfirmPasswordToggle} size='small'>
                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Stack>
                  </Stack>
                </Stack>

                <Stack py={2}>
                  {isUpdatingError && (
                    <Alert severity='error'>{getErrorMessage(errorCreating as AxiosDefaultErrorEntity)}</Alert>
                  )}
                </Stack>

                <Divider sx={{ mb: 1, mt: 2 }} />
                <Stack direction='row' justifyContent='space-between' alignItems='' alignContent='flex-end'>
                  <Stack />
                  <Stack direction='row' justifyContent='flex-end' spacing={2} alignItems='center' sx={{ py: 1 }}>
                    <Button disabled={isSemiLoading} variant='main-table-panel-border' onClick={handleOnCancel}>
                      Cancel
                    </Button>
                    <ButtonLoading
                      disabled={!ChangePasswordValidationSchema.isValidSync(values)}
                      variant='main-table-panel'
                      loading={isSemiLoading}
                      onClick={() => handleSubmit()}
                    >
                      Reset
                    </ButtonLoading>
                  </Stack>
                </Stack>
              </Stack>
            </Modal>
          );
        }}
      </Formik>
    </div>
  );
}
